import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import AuthLayout from '@/layouts/AuthLayout';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const redirectUrl = `${window.location.origin}/reset-password-confirm`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl });

      if (error) {
        setError(error.message);
      } else {
        setIsSubmitted(true);
        toast({ title: 'Reset link sent!', description: 'Check your email for password reset instructions.' });
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      {isSubmitted ? (
        <div className="space-y-8 text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-primary" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-display font-medium text-white tracking-normal">Check your inbox.</h1>
            <p className="text-muted-foreground text-sm">
              We sent a password reset link to{' '}
              <span className="text-white font-medium">{email}</span>
            </p>
          </div>
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full h-12 border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-xl"
              onClick={() => setIsSubmitted(false)}
            >
              Try a different email
            </Button>
            <Link to="/auth/login">
              <Button variant="ghost" className="w-full h-12 text-white/60 hover:text-white hover:bg-white/5 rounded-xl">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to sign in
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="space-y-2">
            <h1 className="text-3xl font-display font-medium text-white tracking-normal">Forgot your password?</h1>
            <p className="text-muted-foreground text-sm">
              Enter your email and we'll send you a link to reset it.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70 text-sm">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-primary"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-xl font-semibold shadow-[0_0_30px_rgba(0,210,180,0.25)] transition-all hover:scale-[1.01]"
              disabled={isLoading}
            >
              {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : 'Send reset link'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Remembered it?{' '}
            <Link to="/auth/login" className="text-primary hover:underline font-medium">Back to sign in</Link>
          </p>
        </div>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
